// ─────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for the backend host.
//
// Everything that talks to the backend — the REST client (src/api/client.ts),
// the session socket (src/hooks/useSessionSocket.ts) and the web/TV build
// configs — resolves the URL from HERE. Never hardcode the host anywhere else.
//
// What we always want, with no manual editing and no per-build hacks:
//   • Running the project locally (dev server)         →  LOCAL backend
//   • Anything deployed (web / tizen / webos / native) →  PROD backend
//
// The host is decided from REALITY at runtime, not from build flags:
//   - Web & TV resolve from `window.location` — the origin the app is served
//     from. A real domain or a packaged TV app talks to PROD; only a localhost
//     dev server talks to LOCAL. (We can't trust `__DEV__` here: RNV ships TV
//     builds in debug scheme, so __DEV__ is true even in production — that bug
//     is what kept shipping localhost to the TVs.)
//   - Native (iOS/Android) has no window.location, so it uses `__DEV__`.
//   - An explicit `API_BASE_URL` env var overrides everything (one-offs).
// ─────────────────────────────────────────────────────────────────────────

export const LOCAL_API = "http://localhost:3001";
export const PROD_API = "https://mapa-api.inorain.tech";

function resolveApiBase(): string {
  // 1) Explicit override always wins (one-off runs / build configs):
  //    `API_BASE_URL=… npm run …`.
  const override =
    process.env.API_BASE_URL || process.env.NEXT_PUBLIC_API_BASE_URL;
  if (override) return override;

  // 2) Web & TV builds (web / tizen / webos run react-native-web in a browser
  //    or TV webview). The ORIGIN the app was served from is the source of
  //    truth — we never guess from build flags, because RNV ships TV builds in
  //    "debug" scheme (__DEV__ === true) even for production, which is exactly
  //    what used to make deployed TVs phone home to localhost.
  //      • real domain  (web/tizen/webos.kidly.family, …) → PROD
  //      • packaged TV app (file:// → empty hostname)      → PROD
  //      • localhost AND a dev server is running           → LOCAL
  //    => a deployed build can no longer ship a localhost backend.
  if (
    typeof window !== "undefined" &&
    window.location &&
    typeof window.location.hostname === "string"
  ) {
    const host = window.location.hostname;
    const isLocalHost =
      host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0";
    const isDevServer = process.env.NODE_ENV === "development";
    return isLocalHost && isDevServer ? LOCAL_API : PROD_API;
  }

  // 3) Native (iOS / Android): the RN dev flag is reliable here — there is no
  //    window.location. Metro dev → LOCAL; release / TestFlight / App Store → PROD.
  return typeof __DEV__ !== "undefined" && __DEV__ === true
    ? LOCAL_API
    : PROD_API;
}

/** The backend origin to use, with any trailing slash stripped. */
export const API_BASE_URL = resolveApiBase().replace(/\/+$/, "");
